import { computed, getCurrentInstance, onMounted, ref } from 'vue';
import SalesReceiptAnalysisAPI from 'dashboard/api/salesTransactionReceiptAnalysis';

const emptyAnalysis = () => ({
  conflicts: [],
  permissions: {},
  progress: { pending: 0, percent: 0, resolved: 0, total: 0 },
  status: 'not_started',
});

const errorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.error || fallback;

const conflictPayload = (conflict, resolution) => ({
  campo: conflict.field,
  campo_label: conflict.label,
  id: conflict.id,
  referencia_id: conflict.reference_id,
  tipo: conflict.type,
  validar_campos: resolution === 'confirmed_registration',
  valor_cadastro: conflict.registered_value,
  valor_edit:
    resolution === 'corrected'
      ? conflict.edited_value || conflict.registered_value
      : '',
  valor_recibo: conflict.receipt_value,
});

export function useSalesReceiptAnalysis({ id, t, notify }) {
  const analysis = ref(emptyAnalysis());
  const loading = ref(false);
  const resolvingId = ref(null);

  const hasPendingConflicts = computed(
    () => Number(analysis.value.progress?.pending || 0) > 0
  );
  const canResolve = computed(() =>
    Boolean(analysis.value.permissions?.can_resolve)
  );

  const load = async () => {
    if (!id.value) return null;
    loading.value = true;
    try {
      const response = await SalesReceiptAnalysisAPI.show(id.value);
      analysis.value = response?.data?.data || emptyAnalysis();
      return analysis.value;
    } catch (error) {
      notify(errorMessage(error, t('SALES.DELIVERY.ANALYSIS.ERRORS.LOAD')));
      return null;
    } finally {
      loading.value = false;
    }
  };

  const saveOcr = async ocr => {
    if (!id.value || !ocr?.trim()) return null;
    try {
      const response = await SalesReceiptAnalysisAPI.saveOcr(id.value, ocr);
      analysis.value = response?.data?.data || analysis.value;
      return analysis.value;
    } catch (error) {
      notify(errorMessage(error, t('SALES.DELIVERY.ANALYSIS.ERRORS.SAVE')));
      return null;
    }
  };

  const resolve = async (conflict, resolution) => {
    if (!id.value || !conflict?.id || !canResolve.value) return null;
    resolvingId.value = conflict.id;
    try {
      await SalesReceiptAnalysisAPI.saveConflict(
        id.value,
        conflictPayload(conflict, resolution)
      );
      return load();
    } catch (error) {
      notify(errorMessage(error, t('SALES.DELIVERY.ANALYSIS.ERRORS.RESOLVE')));
      return null;
    } finally {
      resolvingId.value = null;
    }
  };

  if (getCurrentInstance()) onMounted(load);

  return {
    analysis,
    canResolve,
    hasPendingConflicts,
    load,
    loading,
    resolve,
    resolvingId,
    saveOcr,
  };
}
